import { Stack, Card, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';

import { CustomAvatar } from '@app/components/custom-avatar';
import { Strings } from '@app/constants';
import { DataNotFound, Scrollbar } from '@app/components';
import { AddCheckListItem } from '@app/modules/task-board/taskBoardDetails/add-checklist-item';
import { ICheckList, ITransaction } from '../type';

interface Props {
  checkLists: ICheckList[];
  transactions: ITransaction[];
}

const CheckListAndTransactionCard = ({ checkLists, transactions }: Props) => {
  const theme = useTheme();

  const renderMessageWithBold = (message: string) => {
    const regex = /(['"])(.*?)\1/g;
    const parts: Array<string | { bold: boolean; text: string }> = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(message)) !== null) {
      if (match?.index > lastIndex) {
        parts?.push(message.slice(lastIndex, match.index));
      }
      parts?.push({ bold: true, text: match[2] });
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < message.length) {
      parts?.push(message?.slice(lastIndex));
    }

    return parts?.map((part, index) =>
      typeof part === 'string' ? (
        <span key={index}>{part}</span>
      ) : (
        <strong key={index}>{part.text}</strong>
      )
    );
  };

  const formatTimestamp = (timestamp: string) => {
    const date = dayjs(timestamp);
    if (date?.isSame(dayjs(), 'day')) {
      return date?.fromNow();
    }
    return date?.format('D MMM YYYY, h:mm A');
  };

  return (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        maxHeight: '400px',
      }}
      m={1}
      gap={2}
    >
      <Card
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="subtitle1" p={2}>
          {Strings.taskBoard.checklist}
        </Typography>
        <Scrollbar style={{ maxHeight: '300px' }}>
          <Stack spacing={1} px={2} pb={2}>
            {checkLists?.length > 0 ? (
              checkLists?.map((check: any, index: number) => (
                <Card key={index} sx={{ width: '100%' }}>
                  <AddCheckListItem check={check} key={index} />
                </Card>
              ))
            ) : (
              <DataNotFound height={300} />
            )}
          </Stack>
        </Scrollbar>
      </Card>

      {/* Transactions Card */}
      <Card
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="subtitle1" p={2}>
          {Strings.tasks.transaction}
        </Typography>
        <Scrollbar style={{ maxHeight: '300px' }}>
          <Stack mx={3} pb={2}>
            {transactions?.length > 0 ? (
              transactions?.map((transaction, index) => {
                const name = `${transaction.firstName ?? ''} ${transaction.lastName ?? ''}`?.trim();
                return (
                  <Stack
                    key={index}
                    mt={2}
                    gap={1.5}
                    sx={{ display: 'flex', flexDirection: 'row' }}
                  >
                    <CustomAvatar name={name} color="info" />
                    <Card
                      sx={{
                        width: '100%',
                        minHeight: '45px',
                        borderRadius: '11px',
                        display: 'flex',
                        flexDirection: 'row',
                      }}
                    >
                      <Stack
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          height: '100%',
                          width: '7px',
                        }}
                      />
                      <Stack
                        sx={{
                          width: '100%',
                          justifyContent: 'center',
                          py: 1,
                        }}
                        ml={1}
                      >
                        <Typography sx={{ alignItems: 'center', fontSize: '14px' }}>
                          {renderMessageWithBold(transaction?.message ?? '')}
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: theme.palette.grey[700] }}>
                          {transaction?.timestamp ? formatTimestamp(transaction.timestamp) : ''}
                        </Typography>
                      </Stack>
                    </Card>
                  </Stack>
                );
              })
            ) : (
              <DataNotFound height={300} />
            )}
          </Stack>
        </Scrollbar>
      </Card>
    </Stack>
  );
};

export { CheckListAndTransactionCard };
